// StreakChest.js — Animated streak milestone chest for NeevStar
import React, { useState, useEffect } from 'react';

const css = `
@keyframes sc-popIn {
  0%   { transform: scale(0.3) rotate(-10deg); opacity: 0; }
  65%  { transform: scale(1.08) rotate(2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}
@keyframes sc-chestShake {
  0%,100% { transform: rotate(0deg) scale(1); }
  15%      { transform: rotate(-10deg) scale(1.08); }
  30%      { transform: rotate(10deg) scale(1.12); }
  45%      { transform: rotate(-7deg) scale(1.09); }
  60%      { transform: rotate(7deg) scale(1.06); }
  80%      { transform: rotate(-3deg) scale(1.02); }
}
@keyframes sc-lidFly {
  0%   { transform: translate(-50%,0) rotate(0deg) scale(1); opacity:1; }
  100% { transform: translate(-50%,-100px) rotate(-40deg) scale(0.4); opacity:0; }
}
@keyframes sc-itemReveal {
  0%   { transform: scale(0) translateY(20px); opacity:0; }
  70%  { transform: scale(1.15) translateY(-4px); opacity:1; }
  100% { transform: scale(1) translateY(0); opacity:1; }
}
@keyframes sc-coinRain {
  0%   { transform: translateY(0) rotate(0) scale(1); opacity:1; }
  100% { transform: translateY(80px) rotate(720deg) scale(0.4); opacity:0; }
}
@keyframes sc-glow {
  0%,100% { box-shadow: 0 0 24px rgba(255,214,0,0.3), 0 8px 32px rgba(0,0,0,0.4); }
  50%      { box-shadow: 0 0 56px rgba(255,214,0,0.8), 0 8px 40px rgba(0,0,0,0.5); }
}
@keyframes sc-badgeIn {
  0%   { transform: scale(0) rotate(-20deg); opacity:0; }
  70%  { transform: scale(1.2) rotate(5deg); opacity:1; }
  100% { transform: scale(1) rotate(0); opacity:1; }
}
@keyframes sc-confettiDrop {
  0%   { transform: translateY(-10px) rotate(0deg); opacity:1; }
  100% { transform: translateY(70px) rotate(360deg); opacity:0; }
}
.sc-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.88);
  z-index: 9995;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  font-family: 'Nunito', sans-serif;
}
.sc-card {
  background: linear-gradient(160deg, #0D1B4B 0%, #1A0533 60%, #0A0A2E 100%);
  border: 2.5px solid rgba(255,214,0,0.6);
  border-radius: 28px;
  padding: 28px 24px 22px;
  max-width: 360px;
  width: 100%;
  text-align: center;
  animation: sc-popIn 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards, sc-glow 2.5s ease-in-out 0.6s infinite;
  position: relative;
  overflow: hidden;
}
.sc-chest {
  font-size: 80px;
  display: inline-block;
  cursor: pointer;
  position: relative;
  user-select: none;
}
.sc-chest.shaking { animation: sc-chestShake 0.65s ease-in-out; }
.sc-chest-lid {
  position: absolute;
  font-size: 40px;
  top: -4px; left: 50%;
  transform: translate(-50%, 0);
  pointer-events: none;
}
.sc-chest-lid.flying { animation: sc-lidFly 0.7s ease-in forwards; }
.sc-coin-rain { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.sc-coin {
  position: absolute;
  font-size: 20px;
  animation: sc-coinRain 1s ease-in forwards;
}
.sc-item {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,214,0,0.12);
  border: 1.5px solid rgba(255,214,0,0.35);
  border-radius: 14px;
  padding: 8px 16px;
  margin: 5px 4px;
  animation: sc-itemReveal 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
  opacity: 0;
}
.sc-badge {
  display: inline-block;
  animation: sc-badgeIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.4s both;
}
.sc-btn {
  background: linear-gradient(135deg, #FFD600, #FF9500);
  border: none; border-radius: 16px;
  padding: 14px 28px;
  font-size: 16px; font-weight: 900;
  color: #1A1A2E;
  cursor: pointer;
  font-family: 'Nunito', sans-serif;
  width: 100%; margin-top: 18px;
  box-shadow: 0 6px 20px rgba(255,150,0,0.4);
  transition: transform 0.1s;
}
.sc-btn:active { transform: scale(0.97); }
.sc-share-btn {
  background: rgba(37,211,102,0.15);
  border: 1.5px solid rgba(37,211,102,0.4);
  border-radius: 12px;
  padding: 10px 20px;
  font-size: 13px; font-weight: 900;
  color: #25D366;
  cursor: pointer;
  font-family: 'Nunito', sans-serif;
  width: 100%; margin-top: 10px;
  transition: transform 0.1s;
}
.sc-confetti-piece {
  position: absolute;
  width: 8px; height: 8px;
  border-radius: 2px;
  animation: sc-confettiDrop 1s ease-in forwards;
}
`;

const CHESTS = {
  3:  { name: 'Curious Explorer Chest',     emoji: '📦', color: '#00BCD4', coins: 30,  badge: 'Explorer',  title: null,          crown: false },
  7:  { name: 'Future Innovator Chest',     emoji: '🎁', color: '#8B2BE2', coins: 75,  badge: 'Innovator', title: null,          crown: false },
  14: { name: 'NeevStar Pioneer Chest',     emoji: '💎', color: '#FF2D55', coins: 150, badge: 'Pioneer',   title: 'NeevStar Pioneer', crown: false },
  30: { name: 'City Builder Legend Chest',  emoji: '👑', color: '#FFD600', coins: 300, badge: 'Legend',    title: 'City Builder Legend', crown: true },
};

const CONFETTI_COLORS = ['#FFD600','#FF2D55','#00C851','#0066FF','#8B2BE2','#FF9500'];

export default function StreakChest({ streak, onClose, onCoinsEarned }) {
  const [phase, setPhase] = useState('pre');    // pre | shaking | opening | opened
  const [lidFlying, setLidFlying] = useState(false);
  const [coins, setCoins] = useState([]);
  const [confetti, setConfetti] = useState([]);
  const [itemsVisible, setItemsVisible] = useState([]);

  // Find the right chest for this streak milestone
  const milestoneDay = [30,14,7,3].find(d => streak >= d) || 3;
  const chest = CHESTS[milestoneDay];

  useEffect(() => {
    if (!document.getElementById('sc-css')) {
      const s = document.createElement('style');
      s.id = 'sc-css';
      s.textContent = css;
      document.head.appendChild(s);
    }
    // Auto-start shake after 0.8s
    const t = setTimeout(() => startOpen(), 800);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startOpen = () => {
    setPhase('shaking');
    setTimeout(() => {
      setPhase('opening');
      setLidFlying(true);
      // Coin rain
      setCoins(Array.from({ length: 16 }, (_, i) => ({
        id: i,
        left: `${5 + Math.random() * 90}%`,
        delay: `${Math.random() * 0.5}s`,
      })));
      // Confetti burst
      setConfetti(Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${10 + Math.random() * 40}%`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${Math.random() * 0.4}s`,
        size: 6 + Math.random() * 6,
      })));
      // Reveal items one by one
      setTimeout(() => setItemsVisible(['coins']), 400);
      setTimeout(() => setItemsVisible(['coins','badge']), 800);
      setTimeout(() => {
        setItemsVisible(['coins','badge','title']);
        setPhase('opened');
      }, 1200);
    }, 680);
  };

  const handleClaim = () => {
    onCoinsEarned && onCoinsEarned(chest.coins);
    onClose();
  };

  const handleShare = () => {
    const text = `🏆 I just unlocked the "${chest.name}" on NeevStar!\n${milestoneDay}-day learning streak 🔥\nJoin me at neevstar.com`;
    if (navigator.share) {
      navigator.share({ title: 'NeevStar Streak Chest!', text });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  return (
    <div className="sc-overlay">
      <div className="sc-card">
        {/* Close button */}
        <button onClick={onClose} style={{
          position:'absolute',top:12,right:14,background:'none',border:'none',
          color:'rgba(255,255,255,0.35)',fontSize:22,cursor:'pointer',lineHeight:1,
          fontFamily:"'Nunito',sans-serif",zIndex:2,
        }}>×</button>

        {/* Coin rain */}
        <div className="sc-coin-rain">
          {coins.map(c => (
            <span key={c.id} className="sc-coin"
              style={{ left: c.left, top: '20%', animationDelay: c.delay }}>
              🪙
            </span>
          ))}
          {confetti.map(c => (
            <div key={c.id} className="sc-confetti-piece"
              style={{
                left: c.left, top: c.top,
                background: c.color,
                width: c.size, height: c.size,
                animationDelay: c.delay,
              }} />
          ))}
        </div>

        {/* Streak badge */}
        <div style={{
          display: 'inline-block',
          background: `linear-gradient(135deg, ${chest.color}33, ${chest.color}22)`,
          border: `1.5px solid ${chest.color}66`,
          borderRadius: 20, padding: '4px 14px', marginBottom: 10,
        }}>
          <span style={{ fontSize: 12, fontWeight: 900, color: chest.color }}>
            🔥 {milestoneDay}-DAY STREAK MILESTONE
          </span>
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 900, color: 'white', margin: '0 0 6px', lineHeight: 1.3 }}>
          {chest.name}
        </h2>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', fontWeight: 700, margin: '0 0 16px' }}>
          Tap the chest to open!
        </p>

        {/* Chest */}
        <div style={{ position: 'relative', height: 100, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span
            className={`sc-chest ${phase === 'shaking' ? 'shaking' : ''}`}
            onClick={phase === 'pre' ? startOpen : undefined}
            style={{ opacity: phase === 'pre' ? 1 : phase === 'opening' || phase === 'opened' ? 0.6 : 1 }}
          >
            {chest.emoji}
          </span>
          {(phase === 'opening' || phase === 'opened') && (
            <span className={`sc-chest-lid ${lidFlying ? 'flying' : ''}`}>🔓</span>
          )}
        </div>

        {/* Revealed items */}
        {itemsVisible.includes('coins') && (
          <div className="sc-item" style={{ animationDelay: '0s' }}>
            <span style={{ fontSize: 22 }}>🪙</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: '#FFD600' }}>+{chest.coins} NeevCoins</span>
          </div>
        )}

        {itemsVisible.includes('badge') && chest.badge && (
          <div>
            <div className="sc-item" style={{ animationDelay: '0.1s' }}>
              <span style={{ fontSize: 20 }}>🏅</span>
              <span style={{ fontSize: 15, fontWeight: 900, color: chest.color }}>{chest.badge} Badge</span>
            </div>
          </div>
        )}

        {itemsVisible.includes('title') && chest.title && (
          <div className="sc-badge">
            <div style={{
              background: `linear-gradient(135deg, ${chest.color}33, ${chest.color}11)`,
              border: `1.5px solid ${chest.color}55`,
              borderRadius: 12, padding: '6px 16px', marginTop: 4,
              display: 'inline-block',
            }}>
              <span style={{ fontSize: 13, fontWeight: 900, color: chest.color }}>
                {chest.crown ? '👑' : '🎖️'} "{chest.title}" Title Unlocked!
              </span>
            </div>
          </div>
        )}

        {/* Buttons */}
        {phase === 'opened' && (
          <>
            <button className="sc-btn" onClick={handleClaim}>
              ✅ Claim Reward!
            </button>
            <button className="sc-share-btn" onClick={handleShare}>
              📤 Share on WhatsApp
            </button>
          </>
        )}

        {phase !== 'opened' && (
          <button className="sc-btn" style={{ opacity: 0.5, marginTop: 18 }} disabled>
            Opening...
          </button>
        )}

        {/* Streak progress hint */}
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontWeight: 700, margin: '10px 0 0' }}>
          {streak}-day streak 🔥 Next chest:{' '}
          {milestoneDay === 3 ? '7 days' : milestoneDay === 7 ? '14 days' : milestoneDay === 14 ? '30 days' : '∞ Legend!'}
        </p>
      </div>
    </div>
  );
}
